/**
 * Debug Helpers - Visualization tools for development
 * 
 * Features:
 * - Bounding box visualization
 * - Spawn point markers
 * - Terrain height grid
 * - Physics body wireframes
 * - Collision shape visualization
 * - Map asset bounds display
 * - Performance stats overlay
 */

import { Scene } from "@babylonjs/core/scene";
import { Vector3, Color3 } from "@babylonjs/core/Maths/math";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";

import { IDisposable, IUpdatable } from "../core/Engine.types";
import { Entity } from "../entity/Entity";
import { TerrainManager } from "../terrain/index";

/**
 * Debug visualization options
 */
export interface DebugOptions {
    showBoundingBoxes?: boolean;
    showSpawnPoints?: boolean;
    showTerrainGrid?: boolean;
    showPhysicsBodies?: boolean;
    showCollisionShapes?: boolean;
    showMapAssetBounds?: boolean;
    showGroundRays?: boolean;
    showPerformanceStats?: boolean;
    boundingBoxColor?: Color3;
    spawnPointColor?: Color3;
    gridColor?: Color3;
    collisionColor?: Color3;
    physicsStaticColor?: Color3;
    physicsDynamicColor?: Color3;
}

/**
 * Debug category for filtering
 */
export enum DebugCategory {
    ENTITIES = "entities",
    TERRAIN = "terrain",
    PHYSICS = "physics",
    MAP_ASSETS = "mapAssets",
    SPAWN_POINTS = "spawnPoints",
    PERFORMANCE = "performance",
    ALL = "all",
}

/**
 * Registered debug object info
 */
interface DebugMeshInfo {
    mesh: Mesh | LinesMesh;
    category: DebugCategory;
    sourceId?: string;
}

/**
 * DebugVisualizer - Development visualization tools
 */
export class DebugVisualizer implements IDisposable, IUpdatable {
    private _scene: Scene;
    private _enabled: boolean = false;
    private _options: Required<DebugOptions>;
    
    // Debug meshes by category
    private _debugMeshes: Map<string, DebugMeshInfo> = new Map();
    private _boundingBoxMeshes: Map<string, LinesMesh> = new Map();
    private _spawnPointMeshes: Mesh[] = [];
    private _terrainGridMesh: LinesMesh | null = null;
    private _collisionMeshes: Map<string, Mesh> = new Map();
    private _physicsDebugMeshes: Map<string, Mesh> = new Map();
    
    // Materials
    private _markerMaterial: StandardMaterial | null = null;
    private _wireframeMaterials: Map<string, StandardMaterial> = new Map();
    
    // Category visibility
    private _categoryVisibility: Map<DebugCategory, boolean> = new Map();
    
    // Stats tracking
    private _stats = {
        drawCalls: 0,
        triangles: 0,
        fps: 0,
        frameTime: 0,
        physicsTime: 0,
        entityCount: 0,
        collisionCount: 0,
    };

    public get enabled(): boolean {
        return this._enabled;
    }

    public get stats() {
        return { ...this._stats };
    }

    constructor(scene: Scene, options?: DebugOptions) {
        this._scene = scene;
        this._options = {
            showBoundingBoxes: options?.showBoundingBoxes ?? true,
            showSpawnPoints: options?.showSpawnPoints ?? true,
            showTerrainGrid: options?.showTerrainGrid ?? false,
            showPhysicsBodies: options?.showPhysicsBodies ?? false,
            showCollisionShapes: options?.showCollisionShapes ?? true,
            showMapAssetBounds: options?.showMapAssetBounds ?? true,
            showGroundRays: options?.showGroundRays ?? false,
            showPerformanceStats: options?.showPerformanceStats ?? true,
            boundingBoxColor: options?.boundingBoxColor ?? new Color3(0, 1, 0),
            spawnPointColor: options?.spawnPointColor ?? new Color3(1, 1, 0),
            gridColor: options?.gridColor ?? new Color3(0.5, 0.5, 0.5),
            collisionColor: options?.collisionColor ?? new Color3(0, 0.8, 1),
            physicsStaticColor: options?.physicsStaticColor ?? new Color3(0.5, 0.5, 1),
            physicsDynamicColor: options?.physicsDynamicColor ?? new Color3(1, 0.5, 0),
        };

        // Initialize category visibility
        for (const category of Object.values(DebugCategory)) {
            this._categoryVisibility.set(category, true);
        }

        this._createMaterials();
    }

    /**
     * Enable/disable debug visualization
     */
    public setEnabled(enabled: boolean): void {
        this._enabled = enabled;
        
        // Toggle visibility of all debug meshes
        this._boundingBoxMeshes.forEach(mesh => mesh.setEnabled(enabled && this._options.showBoundingBoxes));
        this._spawnPointMeshes.forEach(mesh => mesh.setEnabled(enabled && this._options.showSpawnPoints));
        this._collisionMeshes.forEach(mesh => mesh.setEnabled(enabled && this._options.showCollisionShapes));
        this._physicsDebugMeshes.forEach(mesh => mesh.setEnabled(enabled && this._options.showPhysicsBodies));
        
        if (this._terrainGridMesh) {
            this._terrainGridMesh.setEnabled(enabled && this._options.showTerrainGrid);
        }
        
        for (const info of this._debugMeshes.values()) {
            const categoryVisible = this._categoryVisibility.get(info.category) ?? true;
            info.mesh.setEnabled(enabled && categoryVisible);
        }
        
        console.log(`DebugVisualizer: ${enabled ? 'Enabled' : 'Disabled'}`);
    }

    /**
     * Toggle debug visualization
     */
    public toggle(): void {
        this.setEnabled(!this._enabled);
    }

    /**
     * Set category visibility
     */
    public setCategoryVisible(category: DebugCategory, visible: boolean): void {
        this._categoryVisibility.set(category, visible);
        this._updateVisibility();
    }

    /**
     * Toggle category visibility
     */
    public toggleCategory(category: DebugCategory): void {
        const current = this._categoryVisibility.get(category) ?? true;
        this.setCategoryVisible(category, !current);
    }

    /**
     * Update visibility based on categories
     */
    private _updateVisibility(): void {
        for (const [, info] of this._debugMeshes) {
            const categoryVisible = this._categoryVisibility.get(info.category) ?? true;
            info.mesh.setEnabled(this._enabled && categoryVisible);
        }
    }

    /**
     * Update method - updates stats if enabled
     */
    public update(_deltaTime: number): void {
        if (!this._enabled || !this._options.showPerformanceStats) return;
        
        // Update performance stats
        this._stats.fps = Math.round(this._scene.getEngine().getFps());
        this._stats.frameTime = this._scene.getEngine().getDeltaTime();
        this._stats.drawCalls = this._scene.getEngine()._drawCalls?.current ?? 0;
    }

    /**
     * Show bounding box for an entity
     */
    public showEntityBounds(entity: Entity): LinesMesh {
        // Remove existing if any
        this.hideEntityBounds(entity);

        const bbox = entity.getBoundingBox();
        
        // Create bounding box lines in LOCAL space (relative to entity)
        // Convert world bbox to local coordinates relative to entity position
        const entityPos = entity.position;
        const localMin = bbox.min.subtract(entityPos);
        const localMax = bbox.max.subtract(entityPos);
        
        const lines = this._createBoundingBoxLines(
            localMin,
            localMax,
            this._options.boundingBoxColor
        );
        lines.name = `debug_bbox_${entity.id}`;
        
        // Parent to entity so it follows movement
        lines.parent = entity.rootNode;
        
        this._boundingBoxMeshes.set(entity.id, lines);
        this._debugMeshes.set(`bbox_${entity.id}`, {
            mesh: lines,
            category: DebugCategory.ENTITIES,
            sourceId: entity.id,
        });
        lines.setEnabled(this._enabled && this._options.showBoundingBoxes);
        
        return lines;
    }

    /**
     * Hide bounding box for an entity
     */
    public hideEntityBounds(entity: Entity): void {
        const mesh = this._boundingBoxMeshes.get(entity.id);
        if (mesh) {
            mesh.dispose();
            this._boundingBoxMeshes.delete(entity.id);
            this._debugMeshes.delete(`bbox_${entity.id}`);
        }
    }

    /**
     * Update bounding box position (call when entity moves)
     */
    public updateEntityBounds(entity: Entity): void {
        if (this._boundingBoxMeshes.has(entity.id)) {
            this.showEntityBounds(entity);
        }
    }

    /**
     * Show collision shape for a map asset
     */
    public showCollisionShape(
        id: string,
        position: Vector3,
        size: Vector3,
        shapeType: "box" | "sphere" | "capsule" | "cylinder" = "box",
        color?: Color3
    ): Mesh {
        // Remove existing if any
        this.hideCollisionShape(id);
        
        const meshColor = color ?? this._options.collisionColor;
        let mesh: Mesh;
        
        switch (shapeType) {
            case "sphere":
                mesh = MeshBuilder.CreateSphere(`debug_collision_${id}`, {
                    diameter: Math.max(size.x, size.y, size.z),
                    segments: 12,
                }, this._scene);
                break;
            case "capsule":
                mesh = MeshBuilder.CreateCapsule(`debug_collision_${id}`, {
                    radius: Math.max(size.x, size.z) / 2,
                    height: size.y,
                    tessellation: 12,
                    capSubdivisions: 4,
                }, this._scene);
                break;
            case "cylinder":
                mesh = MeshBuilder.CreateCylinder(`debug_collision_${id}`, {
                    diameter: Math.max(size.x, size.z),
                    height: size.y,
                    tessellation: 12,
                }, this._scene);
                break;
            default:
                mesh = MeshBuilder.CreateBox(`debug_collision_${id}`, {
                    width: size.x,
                    height: size.y,
                    depth: size.z,
                }, this._scene);
        }
        
        mesh.position = position.clone();
        
        // Create wireframe material
        const mat = this._getWireframeMaterial(`collision_${shapeType}`, meshColor);
        mesh.material = mat;
        
        this._collisionMeshes.set(id, mesh);
        this._debugMeshes.set(`collision_${id}`, {
            mesh,
            category: DebugCategory.MAP_ASSETS,
            sourceId: id,
        });
        mesh.setEnabled(this._enabled && this._options.showCollisionShapes);
        
        return mesh;
    }

    /**
     * Hide collision shape
     */
    public hideCollisionShape(id: string): void {
        const mesh = this._collisionMeshes.get(id);
        if (mesh) {
            mesh.dispose();
            this._collisionMeshes.delete(id);
            this._debugMeshes.delete(`collision_${id}`);
        }
    }

    /**
     * Show physics body visualization
     */
    public showPhysicsAggregate(
        id: string,
        aggregate: PhysicsAggregate,
        isStatic: boolean = true
    ): Mesh | null {
        // Hide existing
        this.hidePhysicsAggregate(id);
        
        const transformNode = aggregate.body?.transformNode;
        if (!transformNode) return null;
        
        const color = isStatic ? this._options.physicsStaticColor : this._options.physicsDynamicColor;
        
        // Create a simple box representation
        // In a real implementation, you'd create the appropriate shape
        const mesh = MeshBuilder.CreateBox(`debug_physics_${id}`, {
            width: 1,
            height: 1,
            depth: 1,
        }, this._scene);
        
        mesh.parent = transformNode;
        const mat = this._getWireframeMaterial(`physics_${isStatic ? 'static' : 'dynamic'}`, color);
        mesh.material = mat;
        
        this._physicsDebugMeshes.set(id, mesh);
        this._debugMeshes.set(`physics_${id}`, {
            mesh,
            category: DebugCategory.PHYSICS,
            sourceId: id,
        });
        mesh.setEnabled(this._enabled && this._options.showPhysicsBodies);
        
        return mesh;
    }

    /**
     * Hide physics aggregate visualization
     */
    public hidePhysicsAggregate(id: string): void {
        const mesh = this._physicsDebugMeshes.get(id);
        if (mesh) {
            mesh.dispose();
            this._physicsDebugMeshes.delete(id);
            this._debugMeshes.delete(`physics_${id}`);
        }
    }

    /**
     * Show NPC collision radius visualization
     * Creates a cylinder around the NPC showing its collision detection area
     */
    public showNPCCollisionRadius(
        id: string,
        entity: Entity,
        radius: number,
        rayHeights: number[],
        color: Color3 = new Color3(1, 0.5, 0)
    ): Mesh[] {
        // Remove existing
        this.hideNPCCollisionRadius(id);
        
        const meshes: Mesh[] = [];
        
        // Create cylinder showing the collision radius
        const maxHeight = Math.max(...rayHeights);
        const cylinder = MeshBuilder.CreateCylinder(`debug_npc_collision_${id}`, {
            diameter: radius * 2,
            height: maxHeight + 0.5,
            tessellation: 16,
        }, this._scene);
        
        // Position at entity feet + half height
        cylinder.parent = entity.rootNode;
        cylinder.position = new Vector3(0, (maxHeight + 0.5) / 2, 0);
        
        const mat = this._getWireframeMaterial(`npc_collision`, color);
        cylinder.material = mat;
        cylinder.setEnabled(this._enabled && this._options.showBoundingBoxes);
        
        meshes.push(cylinder);
        
        // Create rings at each ray height showing detection levels
        for (const rayHeight of rayHeights) {
            const ring = MeshBuilder.CreateTorus(`debug_npc_ray_${id}_${rayHeight}`, {
                diameter: radius * 2,
                thickness: 0.02,
                tessellation: 24,
            }, this._scene);
            ring.parent = entity.rootNode;
            ring.position = new Vector3(0, rayHeight, 0);
            ring.rotation.x = Math.PI / 2;
            
            const ringMat = new StandardMaterial(`debug_npc_ray_mat_${id}_${rayHeight}`, this._scene);
            ringMat.emissiveColor = new Color3(0, 1, 0);
            ringMat.disableLighting = true;
            ring.material = ringMat;
            ring.setEnabled(this._enabled && this._options.showBoundingBoxes);
            
            meshes.push(ring);
        }
        
        // Store for later cleanup
        this._collisionMeshes.set(`npc_${id}`, cylinder);
        this._debugMeshes.set(`npc_collision_${id}`, {
            mesh: cylinder,
            category: DebugCategory.ENTITIES,
            sourceId: id,
        });
        
        return meshes;
    }

    /**
     * Hide NPC collision radius visualization
     */
    public hideNPCCollisionRadius(id: string): void {
        const mesh = this._collisionMeshes.get(`npc_${id}`);
        if (mesh) {
            // Also dispose children (the ring meshes)
            const children = mesh.parent?.getChildMeshes() ?? [];
            children.forEach(child => {
                if (child.name.startsWith(`debug_npc_ray_${id}`)) {
                    child.dispose();
                }
            });
            mesh.dispose();
            this._collisionMeshes.delete(`npc_${id}`);
            this._debugMeshes.delete(`npc_collision_${id}`);
        }
    }

    /**
     * Show a spawn point marker
     */
    public showSpawnPoint(position: Vector3, size: number = 0.5): Mesh {
        const marker = MeshBuilder.CreateSphere(
            `debug_spawn_${Date.now()}`,
            { diameter: size },
            this._scene
        );
        marker.position = position.clone();
        marker.material = this._markerMaterial;
        marker.setEnabled(this._enabled && this._options.showSpawnPoints);
        
        this._spawnPointMeshes.push(marker);
        return marker;
    }

    /**
     * Clear all spawn point markers
     */
    public clearSpawnPoints(): void {
        this._spawnPointMeshes.forEach(mesh => mesh.dispose());
        this._spawnPointMeshes = [];
    }

    /**
     * Show terrain height grid
     */
    public showTerrainGrid(
        terrain: TerrainManager,
        gridSize: number = 10,
        heightOffset: number = 0.1
    ): void {
        this.hideTerrainGrid();

        const bounds = terrain.bounds;
        const points: Vector3[] = [];
        const colors: Color3[] = [];

        // Create grid lines
        for (let x = bounds.minX; x <= bounds.maxX; x += gridSize) {
            for (let z = bounds.minZ; z <= bounds.maxZ; z += gridSize) {
                const y = terrain.getHeightAtPosition(x, z) + heightOffset;
                
                // Horizontal line (X direction)
                if (x + gridSize <= bounds.maxX) {
                    const nextY = terrain.getHeightAtPosition(x + gridSize, z) + heightOffset;
                    points.push(new Vector3(x, y, z));
                    points.push(new Vector3(x + gridSize, nextY, z));
                    colors.push(this._options.gridColor);
                    colors.push(this._options.gridColor);
                }
                
                // Vertical line (Z direction)
                if (z + gridSize <= bounds.maxZ) {
                    const nextY = terrain.getHeightAtPosition(x, z + gridSize) + heightOffset;
                    points.push(new Vector3(x, y, z));
                    points.push(new Vector3(x, nextY, z + gridSize));
                    colors.push(this._options.gridColor);
                    colors.push(this._options.gridColor);
                }
            }
        }

        if (points.length > 0) {
            this._terrainGridMesh = MeshBuilder.CreateLineSystem(
                "debug_terrain_grid",
                {
                    lines: this._pointsToLineSegments(points),
                },
                this._scene
            );
            this._terrainGridMesh.color = this._options.gridColor;
            this._terrainGridMesh.setEnabled(this._enabled && this._options.showTerrainGrid);
        }
    }

    /**
     * Hide terrain grid
     */
    public hideTerrainGrid(): void {
        if (this._terrainGridMesh) {
            this._terrainGridMesh.dispose();
            this._terrainGridMesh = null;
        }
    }

    /**
     * Draw a line between two points
     */
    public drawLine(
        start: Vector3,
        end: Vector3,
        color: Color3 = new Color3(1, 0, 0)
    ): LinesMesh {
        const lines = MeshBuilder.CreateLines(
            `debug_line_${Date.now()}`,
            { points: [start, end] },
            this._scene
        );
        lines.color = color;
        return lines;
    }

    /**
     * Draw a point marker
     */
    public drawPoint(
        position: Vector3,
        color: Color3 = new Color3(1, 0, 0),
        size: number = 0.2
    ): Mesh {
        const sphere = MeshBuilder.CreateSphere(
            `debug_point_${Date.now()}`,
            { diameter: size },
            this._scene
        );
        sphere.position = position.clone();
        
        const material = new StandardMaterial(`debug_point_mat_${Date.now()}`, this._scene);
        material.emissiveColor = color;
        material.disableLighting = true;
        sphere.material = material;
        
        return sphere;
    }

    /**
     * Log entity debug info to console
     */
    public logEntityInfo(entity: Entity): void {
        const bbox = entity.getBoundingBox();
        console.log(`=== Entity Debug: ${entity.name} (${entity.id}) ===`);
        console.log(`Position: ${this._vec3String(entity.position)}`);
        console.log(`Scale: ${this._vec3String(entity.scale)}`);
        console.log(`Bounding Box:`);
        console.log(`  Min: ${this._vec3String(bbox.min)}`);
        console.log(`  Max: ${this._vec3String(bbox.max)}`);
        console.log(`  Size: ${this._vec3String(bbox.size)}`);
        console.log(`  Center: ${this._vec3String(bbox.center)}`);
        console.log(`  Height: ${bbox.size.y.toFixed(2)}`);
        console.log(`Meshes: ${entity.meshes.length}`);
        console.log(`Animations: ${entity.getAnimationNames().join(", ") || "none"}`);
        console.log(`Tags: ${Array.from(entity.tags).join(", ") || "none"}`);
    }

    /**
     * Create bounding box wireframe lines
     */
    private _createBoundingBoxLines(min: Vector3, max: Vector3, color: Color3): LinesMesh {
        const lines = [
            // Bottom face
            [new Vector3(min.x, min.y, min.z), new Vector3(max.x, min.y, min.z)],
            [new Vector3(max.x, min.y, min.z), new Vector3(max.x, min.y, max.z)],
            [new Vector3(max.x, min.y, max.z), new Vector3(min.x, min.y, max.z)],
            [new Vector3(min.x, min.y, max.z), new Vector3(min.x, min.y, min.z)],
            // Top face
            [new Vector3(min.x, max.y, min.z), new Vector3(max.x, max.y, min.z)],
            [new Vector3(max.x, max.y, min.z), new Vector3(max.x, max.y, max.z)],
            [new Vector3(max.x, max.y, max.z), new Vector3(min.x, max.y, max.z)],
            [new Vector3(min.x, max.y, max.z), new Vector3(min.x, max.y, min.z)],
            // Vertical edges
            [new Vector3(min.x, min.y, min.z), new Vector3(min.x, max.y, min.z)],
            [new Vector3(max.x, min.y, min.z), new Vector3(max.x, max.y, min.z)],
            [new Vector3(max.x, min.y, max.z), new Vector3(max.x, max.y, max.z)],
            [new Vector3(min.x, min.y, max.z), new Vector3(min.x, max.y, max.z)],
        ];

        const mesh = MeshBuilder.CreateLineSystem(
            "boundingBox",
            { lines },
            this._scene
        );
        mesh.color = color;
        
        return mesh;
    }

    /**
     * Convert flat point array to line segments
     */
    private _pointsToLineSegments(points: Vector3[]): Vector3[][] {
        const segments: Vector3[][] = [];
        for (let i = 0; i < points.length; i += 2) {
            if (i + 1 < points.length) {
                segments.push([points[i], points[i + 1]]);
            }
        }
        return segments;
    }

    /**
     * Format Vector3 as string
     */
    private _vec3String(v: Vector3): string {
        return `(${v.x.toFixed(2)}, ${v.y.toFixed(2)}, ${v.z.toFixed(2)})`;
    }

    /**
     * Create debug materials
     */
    private _createMaterials(): void {
        this._markerMaterial = new StandardMaterial("debug_marker_mat", this._scene);
        this._markerMaterial.emissiveColor = this._options.spawnPointColor;
        this._markerMaterial.disableLighting = true;
        this._markerMaterial.alpha = 0.7;
    }

    /**
     * Get or create a wireframe material
     */
    private _getWireframeMaterial(name: string, color: Color3): StandardMaterial {
        if (!this._wireframeMaterials.has(name)) {
            const mat = new StandardMaterial(`debug_wireframe_${name}`, this._scene);
            mat.wireframe = true;
            mat.emissiveColor = color;
            mat.disableLighting = true;
            mat.alpha = 0.8;
            this._wireframeMaterials.set(name, mat);
        }
        return this._wireframeMaterials.get(name)!;
    }

    /**
     * Show map asset bounds (box around placed asset)
     */
    public showMapAssetBounds(
        id: string,
        min: Vector3,
        max: Vector3,
        color?: Color3
    ): LinesMesh {
        const meshColor = color ?? this._options.boundingBoxColor;
        const lines = this._createBoundingBoxLines(min, max, meshColor);
        lines.name = `debug_mapAsset_${id}`;
        
        this._debugMeshes.set(`mapAsset_${id}`, {
            mesh: lines,
            category: DebugCategory.MAP_ASSETS,
            sourceId: id,
        });
        lines.setEnabled(this._enabled && this._options.showMapAssetBounds);
        
        return lines;
    }

    /**
     * Hide map asset bounds
     */
    public hideMapAssetBounds(id: string): void {
        const info = this._debugMeshes.get(`mapAsset_${id}`);
        if (info) {
            info.mesh.dispose();
            this._debugMeshes.delete(`mapAsset_${id}`);
        }
    }

    /**
     * Show ground ray for debugging ground detection
     */
    public showGroundRay(
        id: string,
        start: Vector3,
        end: Vector3,
        hit: boolean
    ): LinesMesh {
        // Remove existing
        this.hideGroundRay(id);
        
        const color = hit ? new Color3(0, 1, 0) : new Color3(1, 0, 0);
        const lines = MeshBuilder.CreateLines(`debug_groundRay_${id}`, {
            points: [start, end],
        }, this._scene);
        lines.color = color;
        
        this._debugMeshes.set(`groundRay_${id}`, {
            mesh: lines,
            category: DebugCategory.TERRAIN,
            sourceId: id,
        });
        lines.setEnabled(this._enabled && this._options.showGroundRays);
        
        return lines;
    }

    /**
     * Hide ground ray
     */
    public hideGroundRay(id: string): void {
        const info = this._debugMeshes.get(`groundRay_${id}`);
        if (info) {
            info.mesh.dispose();
            this._debugMeshes.delete(`groundRay_${id}`);
        }
    }

    /**
     * Show all collision shapes for map assets
     */
    public showAllMapAssetCollisions(
        assets: Array<{ id: string; position: Vector3; size: Vector3; shapeType?: string }>
    ): void {
        for (const asset of assets) {
            this.showCollisionShape(
                asset.id,
                asset.position,
                asset.size,
                (asset.shapeType as any) ?? "box"
            );
        }
    }

    /**
     * Hide all collision shapes
     */
    public hideAllCollisions(): void {
        for (const [id] of this._collisionMeshes) {
            this.hideCollisionShape(id);
        }
    }

    /**
     * Update stats (call each frame when stats are enabled)
     */
    public updateStats(stats: Partial<typeof this._stats>): void {
        Object.assign(this._stats, stats);
    }

    /**
     * Get current debug statistics as formatted string
     */
    public getStatsString(): string {
        return [
            `FPS: ${this._stats.fps}`,
            `Frame: ${this._stats.frameTime.toFixed(1)}ms`,
            `Draw Calls: ${this._stats.drawCalls}`,
            `Entities: ${this._stats.entityCount}`,
            `Collisions: ${this._stats.collisionCount}`,
        ].join("\n");
    }

    /**
     * Dispose all debug resources
     */
    public dispose(): void {
        this._boundingBoxMeshes.forEach(mesh => mesh.dispose());
        this._boundingBoxMeshes.clear();
        
        this._spawnPointMeshes.forEach(mesh => mesh.dispose());
        this._spawnPointMeshes = [];
        
        if (this._terrainGridMesh) {
            this._terrainGridMesh.dispose();
            this._terrainGridMesh = null;
        }
        
        this._collisionMeshes.forEach(mesh => mesh.dispose());
        this._collisionMeshes.clear();
        
        this._physicsDebugMeshes.forEach(mesh => mesh.dispose());
        this._physicsDebugMeshes.clear();
        
        for (const info of this._debugMeshes.values()) {
            info.mesh.dispose();
        }
        this._debugMeshes.clear();
        
        if (this._markerMaterial) {
            this._markerMaterial.dispose();
            this._markerMaterial = null;
        }
        
        for (const mat of this._wireframeMaterials.values()) {
            mat.dispose();
        }
        this._wireframeMaterials.clear();
    }
}
